import { SegmentedControl } from "@/design-system/SegmentedControl/SegmentedControl";
import { cn } from "@/lib/utils";
import { switchLeftPanelMode } from "@/redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { findMetaRating } from "@/redux/squadBuilder/findMetaRating";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { useTranslation } from "@repo/shared/i18n";
import { Chemistry } from "@repo/shared/types";
import { captureMessage } from "@sentry/react";
import { memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { APIStatus } from "../../../../../api/APIStatus.type";
import { PlayerRole } from "../../../../../redux/players/playersSlice";
import {
  BASIC_CHEMSTYLE,
  PlayerFilter,
  PlayerToInclude,
  RightPanelMode,
  Squad,
  SquadBuilderPlayerType,
  countActivePlayerFilters,
} from "../../../../../redux/squadBuilder/squadBuilderUtils";
import { IRootState } from "../../../../../redux/store";
import Draggable from "../../../../molecules/Draggable/Draggable";
import Droppable from "../../../../molecules/Droppable/Droppable";
import { SquadBuilderPlayer } from "../../../../molecules/Player/SquadBuilderPlayer/SquadBuilderPlayer";
import Bench from "../Bench/Bench";
import { PitchItem } from "../PitchItem/PitchItem";
import { PitchRoleTile } from "../PitchRoleTile/PitchRoleTile";

// Asset ids, not copy.
const PITCH_ALT = "pitch";
const WATERMARK_ALT = "www.easysbc.io";

// Cards and tiles sit this far (% of pitch) above their formation spot
const SPOT_LIFT = 5.2;

// Phones' Chemistry/Tactic switch; desktop has the left panel stickers
const PitchViewToggle = ({
  tactics,
  className,
}: {
  tactics: boolean;
  className?: string;
}) => {
  const { t } = useTranslation(["squadBuilder", "common"]);
  const dispatch = useDispatch();
  return (
    <SegmentedControl
      className={className}
      label={t("pitch.view")}
      value={tactics ? "tactics" : "chem-overview"}
      onChange={(mode) => dispatch(switchLeftPanelMode(mode))}
      options={[
        { value: "chem-overview", label: t("common:chemistry") },
        { value: "tactics", label: t("common:tactic") },
      ]}
    />
  );
};

type Props = {
  squad: Squad;
  tempSquad: Squad;
  benchItems: SquadBuilderPlayerType[];
  tempBenchItems: SquadBuilderPlayerType[];
  playersToInclude?: PlayerToInclude[];
  statusBuildSquad: APIStatus;
  handleChangePlayerRole: (index: number) => void;
  handleRemovePlayerOrRole: (index: number) => void;
  handleAddPlayer: (index: number) => void;
  handleAddPlayerFilters: (index: number) => void;
  handleMovePlayer: (fromIndex: number, toIndex: number) => void;
  handleOnClickPlayer: (index: number) => void;
  handleOnClickPlayerMobile: (index: number) => void;
  playerRoles: PlayerRole[];
  handleAddEvoPlayer?: (index: number) => void;
  handleChangeChemstyle?: (index: number) => void;
  changingPitchItemOnIndex?: number;
  rightPanelMode?: RightPanelMode;
  playerFilters: PlayerFilter[];
  tabletOrLargerMode?: boolean;
  desktopMode?: boolean;
  disablePlayerInteractions?: boolean;
  formation: string;
  tempFormation: string;
  hidePlayers: boolean;
  tacticsView?: boolean;
};

interface DrawPlayerProps {
  item: any;
  squad: Squad;
  tempSquad: Squad;
  formationObject: any;
  changingPitchItemOnIndex?: number;
  rightPanelMode?: RightPanelMode;
  playerRoles: PlayerRole[];
  handleChangePlayerRole: (index: number) => void;
  handleRemovePlayerOrRole: (index: number) => void;
  handleAddPlayer: (index: number) => void;
  handleAddPlayerFilters: (index: number) => void;
  handleChangeChemstyle?: (index: number) => void;
  handleAddEvoPlayer?: (index: number) => void;
  handleOnClickPlayer: (index: number) => void;
  handleOnClickPlayerMobile: (index: number) => void;
  tabletOrLargerMode?: boolean;
  desktopMode?: boolean;
  isDragging?: boolean;
  tacticsView?: boolean;
  focus?: string;
}

const PlayerOnPitch: React.FC<DrawPlayerProps> = memo(
  ({
    item,
    squad,
    tempSquad,
    formationObject,
    changingPitchItemOnIndex,
    rightPanelMode,
    handleChangePlayerRole,
    handleRemovePlayerOrRole,
    handleAddPlayer,
    handleAddPlayerFilters,
    handleChangeChemstyle,
    handleAddEvoPlayer,
    handleOnClickPlayer,
    handleOnClickPlayerMobile,
    tabletOrLargerMode,
    desktopMode,
    isDragging,
    tacticsView,
    focus,
  }) => {
    const { player, chemIndex, numberOfFilters } = item;
    const tempChemistry = tempSquad.chemistry;
    const chemistry = squad.chemistry;

    const currentChemistry: Chemistry =
      tempChemistry?.playersChemistry[chemIndex] ??
      chemistry.playersChemistry[chemIndex] ??
      0;
    const previousChemistry = chemistry.playersChemistry[chemIndex] ?? 0;
    const isOutOfPosition =
      tempChemistry?.playersOutOfPosition.some(
        (p) => p.positionIndex === chemIndex,
      ) ||
      chemistry.playersOutOfPosition.some((p) => p.positionIndex === chemIndex);

    let metaRatingObject = null;
    try {
      metaRatingObject = findMetaRating({
        metaRatings: player.metaRatings,
        playerRoleId: player.playerRoleId,
        chemistry: currentChemistry,
      });
    } catch (error) {
      metaRatingObject = { metaRating: 0, chemstyleId: 0 };
      captureMessage(error instanceof Error ? error.message : String(error), {
        level: "error",
        tags: {
          component: "PitchLayout Meta Rating Object",
          playerId: player?.resourceId,
          playerRoleId: player?.playerRoleId,
          playerName: player?.cardName,
          evoPathId: player?.evoPathId,
        },
      });
    }

    const metaRating = metaRatingObject?.metaRating || 0;
    const chemStyleId = metaRatingObject?.chemstyleId || BASIC_CHEMSTYLE;

    let metaRatingZeroChem = 0;
    try {
      // No chemstyle filter: at 0 chem every style scores the same, and the
      // backend tags that entry with the GK basic style for keepers, so
      // matching on the outfield basic id dropped every GK's tier.
      metaRatingZeroChem =
        findMetaRating({
          metaRatings: player.metaRatings,
          playerRoleId: player.playerRoleId,
          chemistry: 0,
        })?.metaRating || 0;
    } catch (error) {
      metaRatingZeroChem = 0;
      captureMessage(error instanceof Error ? error.message : String(error), {
        level: "error",
        tags: {
          component: "PitchLayout Meta Rating Zero Chem",
          playerId: player?.resourceId,
          playerRoleId: player?.playerRoleId,
          playerName: player?.cardName,
          evoPathId: player?.evoPathId,
        },
      });
    }

    return (
      <div
        id={"" + player.resourceId}
        key={player.resourceId}
        className={cn(
          // Larger where the pitch has room; phones and 1025-1279px already overlap
          "relative mobile:scale-105 touch-device:scale-100 large-desktop:scale-105",
          item.isTempPlayer && "opacity-75",
          rightPanelMode === "squad-overview" && "pointer-events-none",
        )}
      >
        {tacticsView ? (
          <PitchRoleTile
            name={player.cardName}
            playerRoleId={player.playerRoleId}
            rolesPlus={player.rolesPlus}
            focus={focus}
            position={formationObject?.labels[chemIndex]}
            isActive={
              changingPitchItemOnIndex === chemIndex && !item.isTempPlayer
            }
            onClick={() => handleChangePlayerRole(chemIndex)}
          />
        ) : (
          <SquadBuilderPlayer
            onClickPlayerRole={() => handleChangePlayerRole(chemIndex)}
            handleAddPlayer={() => handleAddPlayer(chemIndex)}
            handleRemovePlayer={() => handleRemovePlayerOrRole(chemIndex)}
            handleChangeChemstyle={() => handleChangeChemstyle?.(chemIndex)}
            handleAddEvoPlayer={() => handleAddEvoPlayer?.(chemIndex)}
            handleAddPlayerFilters={() => handleAddPlayerFilters(chemIndex)}
            showPlayerFilters={numberOfFilters > 0}
            isSelected={
              changingPitchItemOnIndex === chemIndex && !item.isTempPlayer
            }
            onClickPlayer={(e) => {
              if (desktopMode) {
                handleOnClickPlayer(chemIndex);
              } else {
                handleOnClickPlayerMobile(chemIndex);
                e.stopPropagation();
              }
            }}
            player={player}
            playerRoleId={player.playerRoleId}
            isOnPitch={true}
            chemistry={{
              currentChemistry,
              previousChemistry,
              isOutOfPosition,
            }}
            formationPosition={formationObject?.labels[chemIndex]}
            position={
              player?.possiblePositions?.length > 0
                ? !player?.possiblePositions?.includes(
                    formationObject?.labels[chemIndex],
                  )
                  ? player?.preferredPosition
                  : formationObject?.labels[chemIndex]
                : ""
            }
            chemStyleId={chemStyleId}
            metaRating={metaRating}
            metaRatingZeroChem={metaRatingZeroChem}
            tabletOrLargerMode={tabletOrLargerMode}
            desktopMode={desktopMode}
            isDragging={isDragging}
          />
        )}
      </div>
    );
  },
);

const DraggingPlayerOnBench: React.FC<{
  item: SquadBuilderPlayerType;
  playerRoles: PlayerRole[];
  tabletOrLargerMode?: boolean;
  isDragging?: boolean;
  tacticsView?: boolean;
}> = memo(({ item, tabletOrLargerMode, isDragging, tacticsView }) => {
  if (tacticsView) {
    return (
      <PitchRoleTile
        name={item.cardName}
        playerRoleId={item.playerRoleId}
        rolesPlus={item.rolesPlus}
        position={item.position}
      />
    );
  }
  let metaRatingObject = null;
  try {
    metaRatingObject = findMetaRating({
      metaRatings: item.metaRatings,
      playerRoleId: item.playerRoleId,
      chemistry: 0,
    });
  } catch (error) {
    metaRatingObject = { metaRating: 0, chemstyleId: 0 };
    captureMessage(error instanceof Error ? error.message : String(error), {
      level: "error",
      tags: {
        component: "DraggingPlayerOnBench Meta Rating Object",
        playerId: item?.resourceId,
        playerRoleId: item?.playerRoleId,
        playerName: item?.cardName,
        evoPathId: item?.evoPathId,
      },
    });
  }
  const metaRating = metaRatingObject?.metaRating || 0;
  return (
    <SquadBuilderPlayer
      player={item}
      position={item.preferredPosition}
      metaRating={metaRating}
      metaRatingZeroChem={metaRating}
      chemStyleId={0}
      playerRoleId={item.playerRoleId}
      isOnPitch={!tabletOrLargerMode}
      isOnBench={tabletOrLargerMode}
      tabletOrLargerMode={tabletOrLargerMode}
      isDragging={isDragging}
    />
  );
});

interface DrawEmptyPositionProps {
  item: any;
  isOverItem?: number;
  formationObject: any;
  handleAddPlayer: (index: number) => void;
  handleAddPlayerFilters: (index: number) => void;
  handleChangePlayerRole: (index: number) => void;
  changingPitchItemOnIndex?: number;
  rightPanelMode?: RightPanelMode;
  tabletOrLargerMode?: boolean;
  playerRoles?: PlayerRole[];
}

const DrawEmptyPosition: React.FC<DrawEmptyPositionProps> = memo(
  ({
    item,
    isOverItem,
    formationObject,
    handleAddPlayer,
    handleAddPlayerFilters,
    handleChangePlayerRole,
    changingPitchItemOnIndex,
    rightPanelMode,
    tabletOrLargerMode,
  }) => {
    const { chemIndex, x, y, isTempPlayer, numberOfFilters } = item;
    const formationPosition = formationObject?.labels[chemIndex];
    return (
      <div
        key={chemIndex}
        // Above resting cards (z-1) so the filter button isn't covered; hover lifts its menu like a card's
        className={cn("z-2 hover:z-30", isTempPlayer && "opacity-60")}
        style={{
          left: `${x}%`,
          top: `${y - SPOT_LIFT}%`,
          transform: "translate(-50%, -50%)",
          position: "absolute",
        }}
      >
        <Droppable id={chemIndex}>
          <PitchItem
            isOverItem={isOverItem === chemIndex}
            onClickPlayerPlaceHolder={() => handleAddPlayer(chemIndex)}
            onClickFilter={() => handleAddPlayerFilters(chemIndex)}
            onClickRole={() => handleChangePlayerRole(chemIndex)}
            activePlayerPlaceHolder={
              changingPitchItemOnIndex === chemIndex &&
              (rightPanelMode === "add-player" ||
                rightPanelMode === "add-player-role")
            }
            formationPosition={formationPosition}
            numberOfFilters={numberOfFilters}
            tabletOrLargerMode={tabletOrLargerMode}
          />
        </Droppable>
      </div>
    );
  },
);

export const PitchLayout = ({
  squad,
  benchItems,
  tempBenchItems,
  tempSquad,
  handleChangePlayerRole,
  handleRemovePlayerOrRole,
  handleAddPlayer,
  handleAddPlayerFilters,
  handleMovePlayer,
  handleOnClickPlayer,
  handleOnClickPlayerMobile,
  handleChangeChemstyle,
  handleAddEvoPlayer,
  playerRoles,
  changingPitchItemOnIndex,
  rightPanelMode,
  playerFilters,
  tabletOrLargerMode,
  desktopMode,
  disablePlayerInteractions,
  formation,
  tempFormation,
  hidePlayers,
  tacticsView,
}: Props) => {
  const { players } = squad;
  const { players: tempPlayers } = tempSquad;
  const rawFormation = useSelector(
    (state: IRootState) =>
      state.loadedContentReducer.formations[tempFormation || formation],
  );
  // Same tactic the left panel shows
  const tacticPlayerRoles = useSelector(
    ({ defaultSquadBuilderReducer: sb }: IRootState) =>
      (sb.tempChosenTactic ?? sb.chosenTactic)?.playerRoles,
  );
  // Inline rather than the ad-gutter-pad utility: the bench's class list below
  // carries mobile:pb-0, which a class would lose to.
  const paddingBottom = "calc(var(--ad-gutter) + 48px)";

  const formationObject = useMemo(() => {
    if (!rawFormation) return null;

    return {
      labels: rawFormation.positions.slice(0, 11),
      uniquePositions: rawFormation.uniquePositions.slice(0, 11),
      coordinates: rawFormation.pitchCoordinates.map((coord) => ({
        x: coord.x,
        y: coord.y,
      })),
    };
  }, [rawFormation]);
  type PitchItem = {
    x: number;
    y: number;
    w: number;
    h: number;
    player: SquadBuilderPlayerType;
    chemIndex: number;
    tempPlayer: boolean;
    numberOfFilters: number;
    playerRoleId: number;
  };
  const [pitchItems, setPitchItems] = useState<PitchItem[]>(undefined);
  const [activeId, setActiveId] = useState<string>(undefined);
  const [activeData, setActiveData] = useState<{
    left: string;
    top: string;
    height: number;
    width: number;
  } | null>(undefined);
  const [isOverItem, setIsOverItem] = useState<string>(undefined);

  useEffect(() => {
    if (!formationObject) return;
    const tempPitchData: PitchItem[] = [];
    for (let i = 0; i < 11; i++) {
      let player = {} as SquadBuilderPlayerType;
      if (tempPlayers[i]?.playerRoleId || tempPlayers[i]?.resourceId) {
        player = { ...tempPlayers[i] }; // Clone the object to ensure mutability
      } else if (players[i]?.playerRoleId || players[i]?.resourceId) {
        player = { ...players[i] }; // Clone the object to ensure mutability
      }

      const isTempPlayer =
        !!tempPlayers[i].playerRoleId && tempPlayers[i]?.isAIPlayer;

      const chemIndex = i;

      const numberOfFilters = countActivePlayerFilters(
        playerFilters[chemIndex],
      );

      tempPitchData.push({
        x: formationObject.coordinates[chemIndex].x * 100,
        y: formationObject.coordinates[chemIndex].y * 100,
        w: desktopMode ? 174 : 80,
        h: desktopMode ? 222 : 110,
        player,
        chemIndex,
        tempPlayer: isTempPlayer,
        numberOfFilters,
        playerRoleId: player.playerRoleId,
      });
    }
    setPitchItems(tempPitchData);
  }, [players, tempPlayers, playerFilters, desktopMode, formationObject]);

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
    setActiveData(event.active.data);
  };

  const handleDragEnd = (event: any) => {
    setActiveId(null);
    setIsOverItem(null);
    if (event.over?.id >= 0) {
      handleMovePlayer(+event.active?.id, +event.over?.id);
    }
  };

  const handleDragOver = (event: any) => {
    setIsOverItem(event.over?.id);
  };

  return (
    <>
      <DndContext
        collisionDetection={pointerWithin}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        sensors={useSensors(
          useSensor(PointerSensor, {
            activationConstraint: {
              distance: 2,
            },
          }),
          useSensor(TouchSensor),
        )}
        modifiers={[restrictToWindowEdges]}
      >
        <div className="desktop:w-[950px] large-desktop:min-w-[1100px] touch-device:w-[1140px] w-screen desktop:h-[800px] touch-device:relative mt-2 touch-device:mt-36">
          <div className="relative">
            <img
              // Dimmed so the cards stand out
              className="mx-auto w-screen mobile:w-auto brightness-50"
              width={tabletOrLargerMode ? 934 : 375}
              height={tabletOrLargerMode ? 779 : 677}
              src={
                import.meta.env.VITE_APP_ASSETS_BASE_URL +
                (tabletOrLargerMode
                  ? "/misc/pitch.svg"
                  : "/fc24/misc/mobile_pitch_v1.png")
              }
              alt={PITCH_ALT}
            />
            <img
              src="https://fut-content.s3.eu-west-1.amazonaws.com/fc24/misc/easysbc-watermark.png"
              className="absolute w-[135px] desktop:w-[250px] bottom-[16%] left-4 desktop:bottom-[5%] desktop:left-[50px] touch-device:left-[205px]"
              alt={WATERMARK_ALT}
            />
            {/* Phones switch views here; opposite the watermark, the corner no formation fills */}
            {!tabletOrLargerMode && (
              <PitchViewToggle
                tactics={tacticsView}
                className="absolute bottom-[16%] right-2 z-20 [&>button]:px-2.5"
              />
            )}
          </div>
          {!hidePlayers && (
            <>
              <div className="absolute z-10 touch-device:z-20 top-0 desktop:top-4 touch-device:-top-12 w-[95%] desktop:w-[calc(80%)] touch-device:w-[calc(50%)] large-desktop:w-[calc(63%)] inset-x-0 flex items-center justify-center mobile:left-1/2 mobile:-translate-x-1/2 h-[80%] desktop:h-[80%] touch-device:h-[110%]">
                {/* Top to bottom, so a lower card paints over the one above */}
                {[...(pitchItems ?? [])]
                  .sort((a, b) => a.y - b.y)
                  .map((item) => (
                    <div
                      key={item.player?.resourceId || item.chemIndex}
                      className={cn(
                        " overflow-y-auto",
                        (item.tempPlayer || disablePlayerInteractions) &&
                          "pointer-events-none",
                      )}
                    >
                      {item.player?.resourceId ? (
                        <Draggable
                          id={item.chemIndex.toString()}
                          left={`${item.x}%`}
                          top={`${item.y - SPOT_LIFT}%`}
                          height={item.h}
                          width={item.w}
                          /*
                          The selected card is the one showing the remove
                          control on mobile, and that control sits outside the
                          card's own bounds — so the card has to outrank its
                          neighbours or the tap lands on whichever one is
                          painted over it. Same condition as `isSelected`.
                        */
                          elevated={
                            changingPitchItemOnIndex === item.chemIndex &&
                            !item.tempPlayer
                          }
                        >
                          <Droppable id={item.chemIndex} key={item.chemIndex}>
                            <PlayerOnPitch
                              item={item}
                              squad={squad}
                              tempSquad={tempSquad}
                              formationObject={formationObject}
                              changingPitchItemOnIndex={
                                changingPitchItemOnIndex
                              }
                              rightPanelMode={rightPanelMode}
                              playerRoles={playerRoles}
                              handleChangePlayerRole={handleChangePlayerRole}
                              handleRemovePlayerOrRole={
                                handleRemovePlayerOrRole
                              }
                              handleAddPlayer={handleAddPlayer}
                              handleAddPlayerFilters={handleAddPlayerFilters}
                              handleChangeChemstyle={handleChangeChemstyle}
                              handleAddEvoPlayer={handleAddEvoPlayer}
                              handleOnClickPlayer={handleOnClickPlayer}
                              handleOnClickPlayerMobile={
                                handleOnClickPlayerMobile
                              }
                              tabletOrLargerMode={tabletOrLargerMode}
                              desktopMode={desktopMode}
                              tacticsView={tacticsView}
                              focus={tacticPlayerRoles?.[item.chemIndex]?.focus}
                            />
                          </Droppable>
                        </Draggable>
                      ) : tacticsView ? (
                        // Placed like the cards so rows line up
                        <div
                          className="absolute z-1 hover:z-30 -translate-x-1/2 -translate-y-1/2"
                          style={{
                            left: `${item.x}%`,
                            top: `${item.y - SPOT_LIFT}%`,
                          }}
                        >
                          <Droppable id={item.chemIndex}>
                            <PitchRoleTile
                              playerRoleId={item.playerRoleId}
                              focus={tacticPlayerRoles?.[item.chemIndex]?.focus}
                              position={formationObject?.labels[item.chemIndex]}
                              isActive={
                                changingPitchItemOnIndex === item.chemIndex ||
                                parseInt(isOverItem) === item.chemIndex
                              }
                              onClick={() =>
                                handleChangePlayerRole(item.chemIndex)
                              }
                            />
                          </Droppable>
                        </div>
                      ) : (
                        <DrawEmptyPosition
                          item={item}
                          isOverItem={parseInt(isOverItem)}
                          formationObject={formationObject}
                          handleAddPlayer={handleAddPlayer}
                          handleAddPlayerFilters={handleAddPlayerFilters}
                          handleChangePlayerRole={handleChangePlayerRole}
                          changingPitchItemOnIndex={changingPitchItemOnIndex}
                          rightPanelMode={rightPanelMode}
                          tabletOrLargerMode={tabletOrLargerMode}
                          playerRoles={playerRoles}
                        />
                      )}
                    </div>
                  ))}
              </div>
              {+activeId >= 0 && (
                <DragOverlay dropAnimation={null}>
                  {+activeId <= 10 ? (
                    <div
                      style={{
                        position: "absolute",
                        left: activeData?.left,
                        top: activeData?.top,
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      <div className="relative">
                        <PlayerOnPitch
                          item={pitchItems[+activeId]}
                          squad={squad}
                          tempSquad={tempSquad}
                          formationObject={formationObject}
                          playerRoles={playerRoles}
                          tabletOrLargerMode={tabletOrLargerMode}
                          handleChangePlayerRole={handleChangePlayerRole}
                          handleRemovePlayerOrRole={handleRemovePlayerOrRole}
                          handleAddPlayer={handleAddPlayer}
                          handleAddPlayerFilters={handleAddPlayerFilters}
                          handleOnClickPlayer={handleOnClickPlayer}
                          handleOnClickPlayerMobile={handleOnClickPlayerMobile}
                          isDragging={true}
                          tacticsView={tacticsView}
                          focus={tacticPlayerRoles?.[+activeId]?.focus}
                        />
                      </div>
                    </div>
                  ) : (
                    <DraggingPlayerOnBench
                      item={benchItems[+activeId - 11]}
                      playerRoles={playerRoles}
                      tabletOrLargerMode={tabletOrLargerMode}
                      isDragging={true}
                      tacticsView={tacticsView}
                    />
                  )}
                </DragOverlay>
              )}
            </>
          )}
        </div>
        {!hidePlayers && (
          <div
            className="w-full z-10 -mt-16 mobile:mt-0 touch-device:w-[calc(100%-520px)] sb-v2-lg:w-[calc(100%-704px)] flex flex-row mobile:pb-0 px-1 mobile:px-0"
            style={{
              paddingBottom: paddingBottom,
            }}
          >
            <Bench
              benchItems={benchItems}
              tempBenchItems={tempBenchItems}
              isOverItem={parseInt(isOverItem)}
              handleAddPlayer={handleAddPlayer}
              handleChangePlayerRole={handleChangePlayerRole}
              handleOnClickPlayer={handleOnClickPlayer}
              handleOnClickPlayerMobile={handleOnClickPlayerMobile}
              changingPitchItemOnIndex={changingPitchItemOnIndex}
              rightPanelMode={rightPanelMode}
              tabletOrLargerMode={tabletOrLargerMode}
              handleRemovePlayer={handleRemovePlayerOrRole}
              desktopMode={desktopMode}
              tacticsView={tacticsView}
            />
          </div>
        )}
      </DndContext>
    </>
  );
};
